import { 
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef
} from "react";
import { 
    fetchProductCatalogCategorySettings, 
    ProductCatalogLoadingKeys 
} from "../../slices/productCatalogSlice";
import { getCOGSPreviewRequest, COGSLoadingKeys } from "../../slices/cogsPreviewSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useDebounce } from "../../helpers/useDebounce";
import { scrollToTop } from "../../helpers/common";
import { usePageReset } from "../../helpers/usePageReset";
import { useIsMobile } from "../../helpers/useIsMobile";
import { Pagination } from "../../components/Pagination";
import { DemandPlanningFilters } from "../../components/demandplanning/DemandPlanningFilters";
import { productCatalogStatuses } from "../../consts/productCatalog";
import { ProductRowDetails } from "../../components/cogsCanonicalView/ProductRowDetails";
import { WatchlistDrawer } from "../../components/cogsCanonicalView/WatchlistDrawer";
import { CogsCanonicalHeaderToolbar } from "../../components/cogsCanonicalView/CogsCanonicalHeaderToolbar";
import { CogsColumnsDrawer } from "../../components/cogsCanonicalView/CogsColumnsDrawer";
import ViewGovernanceDialog from "../../components/demandplanning/ViewGovernanceDialog";
import {
    COGS_TABLE_COLUMNS,
    DEFAULT_VISIBLE_COGS_COLUMN_IDS,
} from "../../consts/cogsCanonicalColumns";
import { SelectDropdown } from "../../components/SelectDropdown";
import { 
    BookmarkSquareIcon, 
    ChevronDownIcon, 
    ChevronUpIcon,
    InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { OverlayGrayBackground } from "../../components/OverlayGrayBackground";
import axios from "axios";

const MOBILE_COLUMN_ORDER = [
    "supplySku",
    "totalBom",
    "landedCost",
    "weightedCost",
];

const mapPreviewItemToProduct = (product) => ({
    ...product,
    name: product.productName,
    totalBom: product.totalBOM,
    freight: product.freightUnits,
    tariff: product.tariffUnits,
    landedCost: product.landedUnits,
    weightedCost: product.weightedUnits,
    canonicalCost: product.canonicalUnits,
    weightedVsLanded: product.weightedUnitsVsLandedCostUnits,
    controlStatus: product.controlStatusByProduct,
});

const formatSignedCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }
    const numericValue = Number(value);
    const absoluteValue = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    }).format(Math.abs(numericValue));
    if (numericValue > 0) return `+${absoluteValue}`;
    if (numericValue < 0) return `-${absoluteValue}`;
    return absoluteValue;
};

const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value) || 0);
};

const sanitizeFilePart = (value) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "cogs-canonical-view";

const downloadCsv = (filenameRoot, headers, rows) => {
    const csv = [headers, ...rows]
        .map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${sanitizeFilePart(filenameRoot)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
};

const getColumnExportValue = (product, columnId) => {
    switch (columnId) {
        case "supplySku":
            return product.sku ?? "";
        case "product":
            return product.name ?? product.productName ?? "";
        case "variant":
            return product.variant || "—";
        case "totalBom":
            return product.totalBom ?? "";
        case "freightPerUnit":
            return product.freight ?? "";
        case "tariffPerUnit":
            return product.tariff ?? "";
        case "otherLandedPerUnit":
            return "—";
        case "landedCost":
            return product.landedCost ?? "";
        case "weightedCost":
            return product.weightedCost ?? "";
        case "canonicalCost":
            return product.canonicalCost ?? "";
        case "weightedVsLanded":
            return product.weightedVsLanded ?? "";
        case "lastCostChange":
            return product.lastCostChange ?? "";
        case "missingFreight":
            return product.missingFreightFlag ? "true" : "false";
        case "missingBom":
            return product.missingBomFlag ? "true" : "false";
        case "largeVariance":
            return product.largeVarianceFlag ? "true" : "false";
        case "controlStatus":
            return product.controlStatus ?? "";
        default:
            return "";
    }
};

const fetchAllCogsPreviewRows = async (params) => {
    const baseParams = {
        ...params,
        itemsPerPage: 500,
    };
    let page = 1;
    let allItems = [];
    let isTheLastPage = false;

    while (!isTheLastPage) {
        const { data } = await axios.get("/api/v1/get-cogs-preview", {
            params: { ...baseParams, page },
        });
        allItems = allItems.concat(data?.items || []);
        isTheLastPage = Boolean(data?.isTheLastPage);
        page += 1;
        if (page > 100) break;
    }

    return allItems.map(mapPreviewItemToProduct);
};

const getControlTone = ({
    missingBomFlag,
    missingFreightFlag,
    largeVarianceFlag,
    weightedCost,
}) => {
    if (missingBomFlag) return "critical";
    if (missingFreightFlag || largeVarianceFlag) return "warning";
    if (weightedCost == null || weightedCost === "") return "attention";
    return "ok";
};

const getControlToneClassName = (product) => {
    const tone = getControlTone(product);
    const baseClasses = "inline-block max-w-[136px] truncate rounded-md border px-2 py-0.5 text-[10px] font-medium shadow-none";

    if (tone === "critical") {
        return `${baseClasses} border-rose-200 bg-rose-50 text-rose-700`;
    }
    if (tone === "warning") {
        return `${baseClasses} border-amber-200 bg-amber-50 text-amber-700`;
    }
    if (tone === "attention") {
        return `${baseClasses} border-slate-200 bg-slate-100 text-slate-700`;
    }
    return `${baseClasses} border-emerald-200 bg-emerald-50 text-emerald-700`;
};

const renderFlagBadge = (value, trueLabel) => {
    const baseClasses = "inline-block rounded-md border px-2 py-0 text-[10px] font-medium shadow-none";
    if (value) {
        return (
            <span className={`${baseClasses} border-amber-200 bg-amber-50 text-amber-700`}>
                {trueLabel}
            </span>
        );
    }
    return (
        <span className={`${baseClasses} border-border bg-white text-gray-500`}>
            No
        </span>
    );
};

const COGSCanonicalView = ({
    marginFromSidebar
}) => {

    const dispatch = useDispatch();
    const catalogTableRef = useRef(null);
    const isMobile = useIsMobile();
    const { 
        categorySettingsItems, 
        loadingKeys: productCatalogLoadingKeys
     } = useSelector((state) => state.productCatalog);

    const {
        cogsPreview,
        pagination,
        loadingKeys: cogsPreviewLoadingKeys,
        firstLoadComplete,
    } = useSelector((state) => state.cogsPreview);

    const [currentPage, setCurrentPage] = useState(1);
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(100);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const shouldSkipRequest = usePageReset(
        setCurrentPage,
        currentPage,
        'get:/api/v1/get-cogs-preview'
    );

    const [selectedSupplierId, setSelectedSupplierId] = useState("all-suppliers");
    const [selectedControlState, setSelectedControlState] = useState("all-control-states");
    const [selectedCostBasisState, setSelectedCostBasisState] = useState("all-cost-basis-states");
    const [hideStatistic, setHideStatistic] = useState(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState([]);
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState([]);
    const [selectedStatusId, setSelectedStatusId] = useState([]);
    const [selectedProductFromTheTable, setSelectedProductFromTheTable] = useState(null);
    const [showWatchlist, setShowWatchlist] = useState(false);
    const [watchlistRows, setWatchlistRows] = useState([]);
    const [watchlistLoading, setWatchlistLoading] = useState(false);
    const [columnDrawerOpen, setColumnDrawerOpen] = useState(false);
    const [visibleColumnIds, setVisibleColumnIds] = useState(DEFAULT_VISIBLE_COGS_COLUMN_IDS);
    const [governanceDialogOpen, setGovernanceDialogOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(
        `${window.innerWidth - Number(marginFromSidebar)}px`
    );

    useEffect(() => {
        const handleResize = () => {
          setDrawerWidth(
            `${window.innerWidth - Number(marginFromSidebar)}px`
          );
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
        };
    }, [marginFromSidebar]);

    const clearFilters = useCallback(() => {
        setCurrentPage(1);
        setSearchTerm('');
        setSelectedCategoryId([]);
        setSelectedSubcategoryId([]);
        setSelectedStatusId([]);
        setSelectedSupplierId("all-suppliers");
        setSelectedControlState("all-control-states");
        setSelectedCostBasisState("all-cost-basis-states");
    }, []);

    const handleSupplierChange = useCallback((value) => {
        setCurrentPage(1);
        setSelectedSupplierId(value);
    }, []);

    const handleControlStateChange = useCallback((value) => {
        setCurrentPage(1);
        setSelectedControlState(value);
    }, []);

    const handleCostBasisStateChange = useCallback((value) => {
        setCurrentPage(1);
        setSelectedCostBasisState(value);
    }, []);

    const handleCategoryIdChange = useCallback((value) => {
        setCurrentPage(1);
        setSelectedCategoryId(value);
    }, []);

    const handleSubcategoryIdChange = useCallback((value) => {
        setCurrentPage(1);
        setSelectedSubcategoryId(value);
    }, []);

    const handleStatusIdChange = useCallback((value) => {
        setCurrentPage(1);
        setSelectedStatusId(value);
    }, []);

    const handleSearchChange = useCallback((value) => {
        setCurrentPage(1);
        setSearchTerm(value);
    }, []);

    useEffect(() => {
        dispatch(fetchProductCatalogCategorySettings());
    }, [dispatch]);

    useEffect(() => {
        scrollToTop();
        catalogTableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPage, currentItemsPerPage, catalogTableRef]);

    useEffect(() => {
        if (currentPage === 1 && shouldSkipRequest()) {
            return;
        }

        dispatch(getCOGSPreviewRequest({
            searchTerm: debouncedSearchTerm,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
            selectedSupplierId,
            selectedControlState,
            selectedCostBasisState,
            page: currentPage,
            itemsPerPage: currentItemsPerPage,
        }));
    }, [
        dispatch,
        debouncedSearchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        selectedSupplierId,
        selectedControlState,
        selectedCostBasisState,
        currentPage,
        currentItemsPerPage,
        shouldSkipRequest,
    ]);

    const tableProducts = useMemo(
        () => (cogsPreview?.items || []).map(mapPreviewItemToProduct),
        [cogsPreview]
    );

    const fetchWatchlistRows = useCallback(async () => {
        setWatchlistLoading(true);
        try {
            const baseParams = {
                searchTerm: debouncedSearchTerm,
                selectedCategoryId,
                selectedSubcategoryId,
                selectedStatusId,
                selectedSupplierId,
                selectedControlState: "exceptions-only",
                selectedCostBasisState,
                itemsPerPage: 500,
            };

            let page = 1;
            let allItems = [];
            let isTheLastPage = false;

            while (!isTheLastPage) {
                const { data } = await axios.get("/api/v1/get-cogs-preview", {
                    params: { ...baseParams, page },
                });
                allItems = allItems.concat(data?.items || []);
                isTheLastPage = Boolean(data?.isTheLastPage);
                page += 1;
                if (page > 100) break;
            }

            setWatchlistRows(allItems.map(mapPreviewItemToProduct));
        } catch (error) {
            console.error("Failed to load control watchlist:", error);
            setWatchlistRows([]);
        } finally {
            setWatchlistLoading(false);
        }
    }, [
        debouncedSearchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        selectedSupplierId,
        selectedCostBasisState,
    ]);

    useEffect(() => {
        if (showWatchlist) {
            fetchWatchlistRows();
        }
    }, [showWatchlist, fetchWatchlistRows]);

    const summary = cogsPreview?.summary || {};
    const suppliersToSelect = cogsPreview?.suppliers || [];
    
    const categoryFilterOptions = useMemo(
        () =>
          (categorySettingsItems || []).map((group) => ({
            label: group.name,
            value: group.id,
          })),
        [categorySettingsItems]
    );

    const subcategoryFilterOptions = useMemo(() => {
        const map = new Map();
        (categorySettingsItems || []).forEach((group) => {
          (group.variants || []).forEach((variant) => {
            if (!map.has(variant.id)) {
              map.set(variant.id, {
                label: `${group.name} - ${variant.name}`,
                value: variant.id,
              });
            }
          });
        });
        return Array.from(map.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        );
    }, [categorySettingsItems]);

    const isCategorySettingsLoading = productCatalogLoadingKeys.includes(
        ProductCatalogLoadingKeys.fetchCategorySettings
    );
    const isCogsPreviewLoading = cogsPreviewLoadingKeys.includes(
        COGSLoadingKeys.getCOGSPreview
    );

    const visibleColumns = useMemo(() => {
        const columns = COGS_TABLE_COLUMNS.filter((column) => visibleColumnIds.includes(column.id));
        if (!isMobile) return columns;

        const leadingColumns = MOBILE_COLUMN_ORDER.flatMap((columnId) =>
            columns.filter((column) => column.id === columnId)
        );
        const leadingColumnIds = new Set(leadingColumns.map((column) => column.id));
        return [
            ...leadingColumns,
            ...columns.filter((column) => !leadingColumnIds.has(column.id)),
        ];
    }, [visibleColumnIds, isMobile]);

    const exportableColumns = useMemo(
        () => COGS_TABLE_COLUMNS.filter(
            (column) =>
                column.id !== "action" && visibleColumnIds.includes(column.id)
        ),
        [visibleColumnIds]
    );

    const exportFilterParams = useMemo(
        () => ({
            searchTerm: debouncedSearchTerm,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
            selectedSupplierId,
            selectedControlState,
            selectedCostBasisState,
        }),
        [
            debouncedSearchTerm,
            selectedCategoryId,
            selectedSubcategoryId,
            selectedStatusId,
            selectedSupplierId,
            selectedControlState,
            selectedCostBasisState,
        ]
    );

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            const rows = await fetchAllCogsPreviewRows(exportFilterParams);
            downloadCsv(
                "cogs-canonical-view",
                exportableColumns.map((column) => column.label),
                rows.map((row) =>
                    exportableColumns.map((column) => getColumnExportValue(row, column.id))
                )
            );
        } catch (error) {
            console.error("Failed to export COGS canonical view:", error);
        } finally {
            setIsExporting(false);
        }
    }, [exportFilterParams, exportableColumns]);

    const openGovernanceDialog = useCallback(() => {
        setGovernanceDialogOpen(true);
    }, []);

    const columnsCustomized = useMemo(() => {
        if (visibleColumnIds.length !== DEFAULT_VISIBLE_COGS_COLUMN_IDS.length) {
            return true;
        }
        return !DEFAULT_VISIBLE_COGS_COLUMN_IDS.every((id) => visibleColumnIds.includes(id));
    }, [visibleColumnIds]);

    const toggleColumnVisibility = useCallback((columnId) => {
        setVisibleColumnIds((current) => {
            if (current.includes(columnId)) {
                return current.length === 1 ? current : current.filter((id) => id !== columnId);
            }
            return [...current, columnId];
        });
    }, []);

    const resetColumns = useCallback(() => {
        setVisibleColumnIds(DEFAULT_VISIBLE_COGS_COLUMN_IDS);
    }, []);

    const showAllColumns = useCallback(() => {
        setVisibleColumnIds(COGS_TABLE_COLUMNS.map((column) => column.id));
    }, []);

    const openColumnsDrawer = useCallback(() => {
        setColumnDrawerOpen(true);
    }, []);

    const renderTableCell = useCallback((product, column, onInspect) => {
        const cellClass = `border-b border-transparent px-3 py-2 align-middle ${column.cellClassName}`;

        switch (column.id) {
            case "supplySku":
                return (
                    <td className={`whitespace-nowrap text-[13px] font-medium text-primary first:pl-4 ${cellClass}`} key={column.id}>
                        {product.sku}
                    </td>
                );
            case "product":
                return (
                    <td className={`text-[13px] text-primary ${cellClass}`} key={column.id}>
                        <button
                            type="button"
                            className="max-w-[162px] truncate text-left font-medium text-sky-700 transition hover:underline"
                            onClick={() => onInspect(product)}
                        >
                            {product.name}
                        </button>
                    </td>
                );
            case "variant":
                return (
                    <td className={`whitespace-nowrap text-[12px] text-gray-600 ${cellClass}`} key={column.id}>
                        <span className="inline-block max-w-[72px] truncate align-middle">{product.variant || "—"}</span>
                    </td>
                );
            case "totalBom":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary ${cellClass}`} key={column.id}>
                        {product.totalBom}
                    </td>
                );
            case "freightPerUnit":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary ${cellClass}`} key={column.id}>
                        {product.freight}
                    </td>
                );
            case "tariffPerUnit":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary ${cellClass}`} key={column.id}>
                        {product.tariff}
                    </td>
                );
            case "otherLandedPerUnit":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary ${cellClass}`} key={column.id}>
                        —
                    </td>
                );
            case "landedCost":
                return (
                    <td className={`whitespace-nowrap text-[13px] font-medium text-primary ${cellClass}`} key={column.id}>
                        {product.landedCost}
                    </td>
                );
            case "weightedCost":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary ${cellClass}`} key={column.id}>
                        {product.weightedCost}
                    </td>
                );
            case "canonicalCost":
                return (
                    <td className={`whitespace-nowrap text-[13px] font-semibold text-primary ${cellClass}`} key={column.id}>
                        {product.canonicalCost}
                    </td>
                );
            case "weightedVsLanded":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary ${cellClass}`} key={column.id}>
                        {product.weightedVsLanded === null ? (
                            <div className="text-[13px] font-medium text-gray-900">—</div>
                        ) : (
                            <div
                                className={`text-[16px] font-semibold tracking-[-0.02em] ${
                                    product.weightedVsLanded > 0
                                        ? "text-emerald-800"
                                        : product.weightedVsLanded < 0
                                          ? "text-rose-700"
                                          : "text-gray-900"
                                }`}
                            >
                                {formatSignedCurrency(product.weightedVsLanded)}
                            </div>
                        )}
                    </td>
                );
            case "lastCostChange":
                return (
                    <td className={`whitespace-nowrap text-[12px] text-gray-600 ${cellClass}`} key={column.id}>
                        {product.lastCostChange}
                    </td>
                );
            case "missingFreight":
                return (
                    <td className={cellClass} key={column.id}>
                        {renderFlagBadge(product.missingFreightFlag, "Missing")}
                    </td>
                );
            case "missingBom":
                return (
                    <td className={cellClass} key={column.id}>
                        {renderFlagBadge(product.missingBomFlag, "Missing")}
                    </td>
                );
            case "largeVariance":
                return (
                    <td className={cellClass} key={column.id}>
                        {renderFlagBadge(product.largeVarianceFlag, "Flagged")}
                    </td>
                );
            case "controlStatus":
                return (
                    <td className={`text-[13px] text-primary ${cellClass}`} key={column.id}>
                        <span className={getControlToneClassName(product)}>
                            {product.controlStatus}
                        </span>
                    </td>
                );
            case "action":
                return (
                    <td className={`whitespace-nowrap text-[13px] text-primary last:pr-4 ${cellClass}`} key={column.id}>
                        <button
                            type="button"
                            onClick={() => onInspect(product)}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-ring/50 border bg-transparent shadow-xs hover:bg-gray-50 py-2 h-7 rounded-md px-2.5 text-[12px]"
                        >
                            Inspect
                        </button>
                    </td>
                );
            default:
                return null;
        }
    }, []);

    const defaultThClasses = "h-10 whitespace-nowrap border-b border-border bg-white px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-600 first:pl-4 last:pr-4";

    return (
        <>
            <main className="flex h-[calc(100dvh-56px)] min-h-0 w-full flex-col overflow-hidden px-4 sm:px-6 lg:px-8 md:block md:h-full md:overflow-visible">
                <div className="flex h-full min-h-0 flex-col py-3 md:py-6">
                    <section className="flex shrink-0 md:shrink pb-4 flex-col gap-3 h-auto xl:h-[55px] lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex min-w-0 items-end gap-3 w-full justify-between md:w-auto md:justify-start">
                            <h2 className="text-primary truncate text-2xl font-semibold tracking-[-0.03em]">
                                COGS Canonical View
                            </h2>
                            <button
                                type="button"
                                className="shrink-0 rounded-full border border-gray-300 border-border bg-white p-2 shadow-sm transition hover:bg-gray-50 md:hidden"
                                onClick={openGovernanceDialog}
                                title="View governance"
                                aria-label="View governance"
                            >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden />
                            </button>
                        </div>
                        <CogsCanonicalHeaderToolbar
                            onOpenColumnsDrawer={openColumnsDrawer}
                            onExport={handleExport}
                            onOpenGovernanceDialog={openGovernanceDialog}
                            withCustomColumns={columnsCustomized}
                            isExporting={isExporting}
                            hideOnMobile
                        />
                    </section>

                    <div className="shrink-0">
                        <DemandPlanningFilters
                            searchTerm={searchTerm}
                            showUndoRestoreButton={false}
                            onSearchChange={handleSearchChange}
                            categoryFilterOptions={categoryFilterOptions}
                            subcategoryFilterOptions={subcategoryFilterOptions}
                            statusFilterOptions={productCatalogStatuses}
                            selectedCategoryId={selectedCategoryId}
                            selectedSubcategoryId={selectedSubcategoryId}
                            selectedStatusId={selectedStatusId}
                            isCategorySettingsLoading={isCategorySettingsLoading}
                            onCategoryIdChange={handleCategoryIdChange}
                            onSubcategoryIdChange={handleSubcategoryIdChange}
                            onStatusIdChange={handleStatusIdChange}
                            onClearFilters={clearFilters}
                            mobileFiltersSheetEnabled
                        />
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col py-2 md:block">
                        <section className="flex min-h-0 flex-1 flex-col md:mb-4 md:block md:rounded-lg md:border md:border-border md:bg-white md:shadow-sm">
                            <div
                                className="hidden border-b border-border py-4 px-4 md:block"
                            >
                                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-2 flex items-center gap-2">
                                            <span
                                                className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none"
                                            >
                                                COGS Canonical View
                                            </span>
                                            <span
                                                className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 shadow-none"
                                            >
                                                Finance source of truth
                                            </span>
                                        </div>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <h2 className="text-xl font-semibold tracking-[-0.02em] text-primary">
                                                    Review the governed unit cost used for close, reporting, and canonical COGS interpretation
                                                </h2>
                                                {!hideStatistic && (
                                                    <>
                                                        <p className="mt-1 max-w-4xl text-sm text-gray-600">
                                                            <span>This surface preserves one row per </span>
                                                            <span className="font-medium text-primary">
                                                                Execution SKU
                                                            </span>
                                                            <span> and keeps the table as the primary operating view. Use the side drawer for exception review and PO-level cost-change inspection.</span>
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 px-4 py-2 has-[>svg]:px-3 rounded-md bg-white"
                                            onClick={() => setShowWatchlist(true)}
                                        >
                                            <BookmarkSquareIcon className="h-4 w-4 text-primary" />
                                            <span>
                                                Control watchlist
                                            </span>
                                            <span className="ml-2 rounded-md border border-border bg-white px-1.5 py-0.5 text-[11px] leading-none text-primary">
                                                {isCogsPreviewLoading ? <LoaderSpinner width={8} height={8} border={1} colorSpinner="black"/> : formatNumber(summary.exceptionCount ?? 0)}
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => setHideStatistic(!hideStatistic)}
                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-2 has-[>svg]:px-3 h-10 rounded-md bg-white px-3"
                                        >
                                            {hideStatistic ? <ChevronDownIcon className="h-4 w-4 text-primary" /> : <ChevronUpIcon className="h-4 w-4 text-primary" />}
                                            <span className="">
                                                Collapse
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                {!hideStatistic && (
                                    <>
                                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-[repeat(3,minmax(180px,0.72fr))_auto_auto]">
                                            <SelectDropdown
                                                options={[
                                                    { label: "All Suppliers", value: "all-suppliers" },
                                                    ...(suppliersToSelect || [])
                                                ]}
                                                selectClassname="!bg-white"
                                                dropdownClassname="!bg-white"
                                                dropdownListClassname="!bg-white"
                                                itemValueClassname="!text-primary hover:!bg-primary hover:!text-white"
                                                selectedItemValueClassname="!bg-primary !text-white"
                                                value={selectedSupplierId}
                                                onChange={handleSupplierChange}
                                            />
                                            <SelectDropdown
                                                options={[
                                                    { label: "All control states", value: "all-control-states" },
                                                    { label: "Exceptions only", value: "exceptions-only" },
                                                    { label: "Missing freight", value: "missing-freight" },
                                                    { label: "Missing BOM", value: "missing-bom" },
                                                    { label: "Weighted cost pending", value: "weighted-cost-pending" },
                                                    { label: "Governed / clean only", value: "governed-clean-only" },
                                                ]}
                                                selectClassname="!bg-white"
                                                dropdownClassname="!bg-white"
                                                dropdownListClassname="!bg-white"
                                                itemValueClassname="!text-primary hover:!bg-primary hover:!text-white"
                                                selectedItemValueClassname="!bg-primary !text-white"
                                                value={selectedControlState}
                                                onChange={handleControlStateChange}
                                            />
                                            <SelectDropdown
                                                options={[
                                                    { label: "All cost-basis states", value: "all-cost-basis-states" },
                                                    { label: "Landed cost only", value: "landed-cost-only" },
                                                    { label: "Weighted cost available", value: "weighted-cost-available" },
                                                    { label: "Recent cost change", value: "recent-cost-change" },
                                                ]}
                                                selectClassname="!bg-white"
                                                dropdownClassname="!bg-white"
                                                dropdownListClassname="!bg-white"
                                                itemValueClassname="!text-primary hover:!bg-primary hover:!text-white"
                                                selectedItemValueClassname="!bg-primary !text-white"
                                                value={selectedCostBasisState}
                                                onChange={handleCostBasisStateChange}
                                            />
                                            <div className="rounded-md border border-border bg-white px-3 py-2">
                                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                    SKUs in scope
                                                </p>
                                                <p className="mt-1 text-sm font-semibold text-primary">
                                                    {isCogsPreviewLoading ? <LoaderSpinner width={20} height={20} border={2} colorSpinner="black"/> : formatNumber(summary.skuCount)}
                                                </p>
                                            </div>
                                            <div className="rounded-md border border-border bg-white px-3 py-2">
                                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                    Canonical coverage
                                                </p>
                                                <p className="mt-1 text-sm font-semibold text-primary">
                                                    {isCogsPreviewLoading ? <LoaderSpinner width={20} height={20} border={2} colorSpinner="black"/> : formatNumber(summary.canonicalCoverageCount)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="mt-3 grid gap-2 md:grid-cols-4">
                                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                    Missing freight
                                                </p>
                                                <p className="mt-1 text-lg font-semibold text-primary">
                                                    {isCogsPreviewLoading ? <LoaderSpinner width={28} height={28} border={1} colorSpinner="black"/> : formatNumber(summary.missingFreightCount)}
                                                </p>
                                            </div>
                                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                    Missing BOM
                                                </p>
                                                <p className="mt-1 text-lg font-semibold text-primary">
                                                    {isCogsPreviewLoading ? <LoaderSpinner width={28} height={28} border={1} colorSpinner="black"/> : formatNumber(summary.missingBomCount)}
                                                </p>
                                            </div>
                                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                    Weighted cost coverage
                                                </p>
                                                <p className="mt-1 text-lg font-semibold text-primary">
                                                    {isCogsPreviewLoading ? <LoaderSpinner width={28} height={28} border={1} colorSpinner="black"/> : formatNumber(summary.weightedCoverageCount)}
                                                </p>
                                            </div>
                                            <div className="rounded-md border border-border bg-background px-3 py-2">
                                                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">
                                                    Suppliers
                                                </p>
                                                <p className="mt-1 text-lg font-semibold text-primary">
                                                    {isCogsPreviewLoading ? <LoaderSpinner width={28} height={28} border={1} colorSpinner="black"/> : formatNumber(summary.supplierCount)}
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="min-h-0 flex-1 overflow-hidden px-0 py-2 md:block md:flex-none md:px-4 md:py-4">
                                <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white md:block md:h-auto">
                                    <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-contain md:block md:flex-none md:overflow-visible">
                                        <div
                                            className="h-full min-h-0 w-max min-w-full overflow-y-auto overscroll-y-contain md:w-auto md:max-h-[72vh] md:overflow-auto"
                                            ref={catalogTableRef}
                                        >
                                            {isCogsPreviewLoading ? (
                                                <div className="flex min-h-[max(200px,100%)] items-center justify-center md:min-h-0 md:py-5">
                                                    <LoaderSpinner width={60} height={60} border={3} colorSpinner="black"/>
                                                </div>
                                            ) : (
                                                <table className="min-w-full border-separate border-spacing-0 text-[13px]">
                                                    <thead className="sticky top-0 z-10 bg-white">
                                                        <tr className="text-left">
                                                            {visibleColumns.map((column) => (
                                                                <th
                                                                    className={`${defaultThClasses} ${column.headerClassName}`}
                                                                    key={column.id}
                                                                >
                                                                    {column.label}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {tableProducts.map((product) => (
                                                            <tr key={product.id} className="group hover:bg-white">
                                                                {visibleColumns.map((column) =>
                                                                    renderTableCell(
                                                                        product,
                                                                        column,
                                                                        setSelectedProductFromTheTable
                                                                    )
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                    {firstLoadComplete && (
                                        <Pagination
                                            currentItemsPerPage={currentItemsPerPage}
                                            rangeRows={pagination.rangeRows}
                                            additionalRanges={[
                                                { value: 250, label: "250" },
                                                { value: 500, label: "500" },
                                            ]}
                                            currentPage={currentPage}
                                            setCurrentPage={setCurrentPage}
                                            isTheLastPage={pagination.isTheLastPage}
                                            totalPages={pagination.totalPages}
                                            setCurrentItemsPerPage={setCurrentItemsPerPage}
                                        />
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </main>
            <ProductRowDetails
                marginFromSidebar={marginFromSidebar}
                drawerWidth={drawerWidth}
                isOpen={!!selectedProductFromTheTable}
                onClose={() => setSelectedProductFromTheTable(null)}
                selectedProduct={selectedProductFromTheTable}
                onBackToWatchlist={() => {
                    setSelectedProductFromTheTable(null);
                    setShowWatchlist(true);
                }}
            />
            <WatchlistDrawer
                marginFromSidebar={marginFromSidebar}
                drawerWidth={drawerWidth}
                isOpen={showWatchlist}
                onClose={() => setShowWatchlist(false)}
                exceptionRows={watchlistRows}
                exceptionCount={watchlistRows.length || summary.exceptionCount || 0}
                isLoading={watchlistLoading}
                onSelectItem={(product) => {
                    setShowWatchlist(false);
                    setSelectedProductFromTheTable(product);
                }}
            />
            <CogsColumnsDrawer
                marginFromSidebar={marginFromSidebar}
                isOpen={columnDrawerOpen}
                onClose={() => setColumnDrawerOpen(false)}
                visibleColumnIds={visibleColumnIds}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetColumns}
                onShowAllColumns={showAllColumns}
            />
            <ViewGovernanceDialog
                open={governanceDialogOpen}
                onOpenChange={setGovernanceDialogOpen}
                viewId="cogs-canonical-view"
                viewName="COGS Canonical View"
                marginFromSidebar={marginFromSidebar}
            />
        </>
    )
};

export default COGSCanonicalView;